import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { supportService, SupportTicket, TicketMessage } from '../../services/supportService';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  UserCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const SupportTicketDetail: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (ticketId) {
      loadTicket();
    }
  }, [ticketId]);

  const loadTicket = async () => {
    if (!ticketId) return;
    
    try {
      setLoading(true);
      const data = await supportService.getTicket(ticketId);
      setTicket(data);
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Failed to load ticket:', error);
      alert('Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!ticketId || !newMessage.trim()) return;

    try {
      setSending(true);
      await supportService.addMessage(ticketId, {
        message: newMessage,
        isInternal,
      });
      setNewMessage('');
      setIsInternal(false);
      await loadTicket();
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleAssignToMe = async () => {
    if (!ticketId || !user?.id) return;

    try {
      setActionLoading(true);
      await supportService.assignTicket(ticketId, user.id);
      await loadTicket();
    } catch (error) {
      console.error('Failed to assign ticket:', error);
      alert('Failed to assign ticket');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateStatus = async (newStatus: string) => {
    if (!ticketId) return;

    try {
      setActionLoading(true);
      await supportService.updateStatus(ticketId, newStatus);
      await loadTicket();
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEscalate = async () => {
    if (!ticketId) return;

    try {
      setActionLoading(true);
      await supportService.escalateTicket(ticketId);
      await loadTicket();
    } catch (error) {
      console.error('Failed to escalate ticket:', error);
      alert('Failed to escalate ticket');
    } finally {
      setActionLoading(false);
    }
  };

  const getPriorityBadge = (priority: string) => {
    const priorityColors = {
      LOW: 'bg-gray-100 text-gray-800',
      MEDIUM: 'bg-blue-100 text-blue-800',
      HIGH: 'bg-yellow-100 text-yellow-800',
      URGENT: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityColors[priority as keyof typeof priorityColors]}`}>
        {priority}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const statusColors = {
      OPEN: 'bg-blue-100 text-blue-800',
      IN_PROGRESS: 'bg-yellow-100 text-yellow-800',
      RESOLVED: 'bg-green-100 text-green-800',
      CLOSED: 'bg-gray-100 text-gray-800',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status as keyof typeof statusColors]}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="text-center">
          <p className="text-gray-500">Ticket not found</p>
          <button
            onClick={() => navigate('/support')}
            className="mt-4 text-orange-600 hover:text-orange-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const isSupport = user?.isSupport || user?.isAdmin;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/support')}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Dashboard
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Ticket #{ticket.id.slice(-8)} - {ticket.subject}
            </h1>
            <div className="flex items-center space-x-2 mt-2">
              {getPriorityBadge(ticket.priority)}
              {getStatusBadge(ticket.status)}
              <span className="text-sm text-gray-500">
                Category: {ticket.category}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Messages */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Conversation</h2>
            </div>

            <div className="p-6 space-y-4 max-h-[600px] overflow-y-auto">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex items-start space-x-3 ${
                    message.isInternal ? 'bg-yellow-50 p-4 rounded-lg' : ''
                  }`}
                >
                  <div className="flex-shrink-0">
                    {message.sender?.avatar ? (
                      <img
                        src={message.sender.avatar}
                        alt={message.sender.username}
                        className="h-10 w-10 rounded-full"
                      />
                    ) : (
                      <UserCircleIcon className="h-10 w-10 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium text-gray-900">
                        {message.sender?.username || 'Unknown'}
                      </p>
                      {(message.sender?.isSupport || message.sender?.isAdmin) && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          Support
                        </span>
                      )}
                      {message.isInternal && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          Internal Note
                        </span>
                      )}
                      <span className="text-xs text-gray-500">
                        {new Date(message.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">
                      {message.message}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Reply Form */}
            {ticket.status !== 'CLOSED' && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="space-y-3">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    rows={4}
                    placeholder="Type your message..."
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                  />
                  <div className="flex items-center justify-between">
                    {isSupport && (
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={isInternal}
                          onChange={(e) => setIsInternal(e.target.checked)}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Internal Note (not visible to user)</span>
                      </label>
                    )}
                    <button
                      onClick={handleSendMessage}
                      disabled={sending || !newMessage.trim()}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                    >
                      <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                      {sending ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Ticket Info */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Ticket Information</h3>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-gray-500">User</dt>
                <dd className="mt-1 text-gray-900">{ticket.user?.username || 'Unknown'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Email</dt>
                <dd className="mt-1 text-gray-900">{ticket.user?.email || 'Unknown'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Assigned To</dt>
                <dd className="mt-1 text-gray-900">
                  {ticket.assignedTo?.username || 'Unassigned'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Created</dt>
                <dd className="mt-1 text-gray-900">
                  {new Date(ticket.createdAt).toLocaleString()}
                </dd>
              </div>
              {ticket.resolvedAt && (
                <div>
                  <dt className="text-gray-500">Resolved</dt>
                  <dd className="mt-1 text-gray-900">
                    {new Date(ticket.resolvedAt).toLocaleString()}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Actions (Support only) */}
          {isSupport && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-sm font-medium text-gray-900 mb-4">Actions</h3>
              <div className="space-y-3">
                {!ticket.assignedToId && (
                  <button
                    onClick={handleAssignToMe}
                    disabled={actionLoading}
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                  >
                    Assign to Me
                  </button>
                )}

                {ticket.status === 'OPEN' && (
                  <button
                    onClick={() => handleUpdateStatus('IN_PROGRESS')}
                    disabled={actionLoading}
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    Start Working
                  </button>
                )}

                {(ticket.status === 'OPEN' || ticket.status === 'IN_PROGRESS') && (
                  <button
                    onClick={() => handleUpdateStatus('RESOLVED')}
                    disabled={actionLoading}
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    Mark as Resolved
                  </button>
                )}

                {ticket.status === 'RESOLVED' && (
                  <button
                    onClick={() => handleUpdateStatus('CLOSED')}
                    disabled={actionLoading}
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                  >
                    Close Ticket
                  </button>
                )}

                {ticket.priority !== 'URGENT' && (
                  <button
                    onClick={handleEscalate}
                    disabled={actionLoading}
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                    Escalate to Urgent
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportTicketDetail;
